import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.scss';

const PrivacyPolicy: React.FC = () => {
  const lastUpdated = 'December 2024';

  return (
    <div className="privacy-policy-container">
      <div className="content-wrapper">
        <div className="header-section">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: {lastUpdated}</p>
        </div>

        <div className="policy-content">
          <section className="policy-section">
            <h2>1. Introduction</h2>
            <p>Florida First Psychiatry ("we," "our," or "us") is committed to protecting your privacy and the confidentiality of your health information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.</p>
            <p>This policy applies to information we collect on our website, through electronic communications, and in connection with our psychiatric and mental health services. Please note that your health information is also protected by the Health Insurance Portability and Accountability Act (HIPAA) and our Notice of Privacy Practices.</p>
          </section>

          <section className="policy-section">
            <h2>2. Information We Collect</h2>
            <h3>2.1 Personal Information</h3>
            <p>We may collect personal information that you voluntarily provide to us, including:</p>
            <ul>
              <li>Name, address, and contact information</li>
              <li>Date of birth and demographic information</li>
              <li>Insurance information</li>
              <li>Medical history and health information</li>
              <li>Appointment scheduling information</li>
              <li>Payment and billing information</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <p>When you visit our website, we may automatically collect certain information, including:</p>
            <ul>
              <li>IP address and browser type</li>
              <li>Pages visited and time spent on our site</li>
              <li>Device information and operating system</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect for various purposes, including:</p>
            <ul>
              <li>Providing psychiatric evaluation and mental health treatment services</li>
              <li>Scheduling and managing appointments</li>
              <li>Processing payments and insurance claims</li>
              <li>Communicating with you about your treatment and care</li>
              <li>Coordinating care with other healthcare providers when authorized</li>
              <li>Improving our website and services</li>
              <li>Complying with legal and regulatory requirements, including HIPAA</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>4. Information Sharing and Disclosure</h2>
            <p>We do not sell, trade, or otherwise transfer your personal or health information to third parties without your consent, except in the following circumstances:</p>
            <ul>
              <li><strong>Treatment:</strong> Sharing information with other healthcare providers involved in your care</li>
              <li><strong>Payment:</strong> Sharing information with insurance companies for billing and payment purposes</li>
              <li><strong>Healthcare Operations:</strong> Sharing information for quality improvement and business operations</li>
              <li><strong>Service Providers:</strong> Sharing information with trusted service providers who assist in our operations (e.g., billing services, IT support) under strict confidentiality agreements</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or public health authorities</li>
              <li><strong>Safety:</strong> To protect the safety of you or others, including reporting requirements for certain situations</li>
              <li><strong>With Your Authorization:</strong> When you provide written authorization for specific disclosures</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>5. Data Security</h2>
            <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
            <ul>
              <li>Encryption of sensitive data</li>
              <li>Secure servers and networks</li>
              <li>Regular security assessments</li>
              <li>Employee training on data protection</li>
              <li>Access controls and authentication</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>6. Your Rights</h2>
            <p>You have certain rights regarding your personal information, including:</p>
            <ul>
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your information (subject to legal requirements)</li>
              <li><strong>Portability:</strong> Request a copy of your information in a portable format</li>
              <li><strong>Opt-out:</strong> Opt out of certain communications</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>7. Cookies and Tracking Technologies</h2>
            <p>Our website may use cookies and similar technologies to enhance your experience. You can control cookie settings through your browser preferences. Please note that disabling cookies may affect website functionality.</p>
          </section>

          <section className="policy-section">
            <h2>8. Third-Party Links</h2>
            <p>Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review their privacy policies.</p>
          </section>

          <section className="policy-section">
            <h2>9. Children's Privacy</h2>
            <p>Our services are primarily intended for adults. We may provide services to adolescents (ages 10 and above) with appropriate parental or guardian consent. We comply with all applicable laws regarding the privacy of minors' health information. If you believe we have collected information from a child inappropriately, please contact us immediately.</p>
          </section>

          <section className="policy-section">
            <h2>10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.</p>
          </section>

          <section className="policy-section">
            <h2>11. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or our privacy practices, please contact us:</p>
            <div className="contact-info">
              <p><strong>Florida First Psychiatry</strong></p>
              <p>1820 Miccosukee Commons Drive<br />Tallahassee, FL 32308</p>
              <p>Phone: (850) 765-8120</p>
              <p>Email: info@ffps.pro</p>
            </div>
            <p>For questions about your health information rights under HIPAA, please refer to our <Link to="/hipaa-notice">HIPAA Notice of Privacy Practices</Link>.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

